// Spec vs Reality — joins the static Bot Catalog against observed sibling-
// session handoffs from the current window.
//
// Per autonomous router bot (bots whose catalog entry declares custom-event
// exits), compare DECLARED destinations against OBSERVED destinations:
//
//   DECLARED  = catalog.bots[botName].declared_custom_events
//   OBSERVED  = unique agentNames of sibling AI sessions that appeared on
//               the same interactionId AFTER this bot's session ended
//               (via lib/sessionClassification.transitionsFromSessions)
//
// Three classes per row:
//   ✓ Match     — observed destination maps to a declared custom event
//                  (or is the __HUMAN__ sentinel for UrgentTransfer /
//                   ExternalTransfer / live-agent handoff)
//   ⚠ Drift     — observed destination has no corresponding declared event
//                  (new feature NOT in spec, OR orchestration-level routing)
//   ○ Unused    — declared event with zero observed handoffs in this window
//                  (dead code OR legitimately rare path)
//
// Pure client-side projection. Reuses the same transitionsFromSessions
// utility BotEffectiveness + RoutingFlow use.

import React, { useMemo } from 'react';
import { Alert, Badge, Group, Stack, Table, Text, Title, Tooltip } from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import catalogData from '../data/botCatalog.json';
import { ReportTable } from './ReportTable';
import { transitionsFromSessions } from '../lib/sessionClassification';

const REPORT_ID = 'spec-vs-reality';
const HUMAN_SINK = '__HUMAN__';

// Which catalog `declared_custom_events` values map to the HUMAN sink.
// If OrthoMain emits UrgentTransfer or ExternalTransfer, the sibling-session
// walk sees that as a human-landing edge (not a sibling AI session). Treat
// these catalog events as satisfied when we observe the HUMAN sink.
const HUMAN_EVENTS = new Set(['UrgentTransfer', 'ExternalTransfer', 'UrgentOperatorRoute']);

// Which catalog events map to which sibling-bot agentName (observed on
// prod). Fuzzy match — we assume a declared event `RoutetoPatientType`
// corresponds to any observed bot whose name contains "PatientType".
// Explicit overrides go here when the convention breaks down.
function matchesEvent(observedBotName, declaredEvent) {
  if (!observedBotName || !declaredEvent) return false;
  if (observedBotName === HUMAN_SINK) return HUMAN_EVENTS.has(declaredEvent);
  const bot = observedBotName.toLowerCase();
  const ev = declaredEvent.toLowerCase().replace(/^routeto/, '');
  if (ev === 'mychartmessage') return bot.includes('mychartmessage');
  if (ev === 'patienttype') return bot.includes('patienttype') || bot.includes('visittype') || bot.includes('appttype');
  if (ev === 'patientregistration') return bot.includes('patientregistration');
  // generic contains-match
  return bot.includes(ev);
}

const COLUMNS = [
  {
    id: 'bot',
    label: 'Router bot',
    pinned: true,
    render: (r) => (
      <Group gap={6}>
        <Text size="sm" fw={500}>{r.bot}</Text>
        <Badge size="xs" color="blue" variant="light">{r.declaredCount} declared</Badge>
      </Group>
    ),
  },
  {
    id: 'match',
    label: '✓ Match',
    hint: 'Observed destination that cleanly corresponds to a declared custom-event exit on this bot.',
    align: 'right',
    render: (r) => (
      <Tooltip label={r.matches.map((m) => `${m.event} → ${m.destination} (${m.count})`).join('; ') || 'None'} multiline w={320}>
        <Badge size="sm" color="teal" variant={r.matches.length > 0 ? 'filled' : 'light'}>
          {r.matches.length}
        </Badge>
      </Tooltip>
    ),
  },
  {
    id: 'drift',
    label: '⚠ Drift',
    hint: 'Observed destination with no matching declared custom-event exit. New feature not in spec, OR an orchestration-layer route the bot catalog doesn\'t capture.',
    align: 'right',
    render: (r) => (
      <Tooltip label={r.drift.map((d) => `${d.destination} (${d.count})`).join('; ') || 'None'} multiline w={320}>
        <Badge size="sm" color={r.drift.length > 0 ? 'orange' : 'gray'} variant={r.drift.length > 0 ? 'filled' : 'light'}>
          {r.drift.length}
        </Badge>
      </Tooltip>
    ),
  },
  {
    id: 'unused',
    label: '○ Unused',
    hint: 'Declared custom-event exit with zero observed handoffs in this window. Dead code OR legitimately rare path.',
    align: 'right',
    render: (r) => (
      <Tooltip label={r.unused.join('; ') || 'None'} multiline w={320}>
        <Badge size="sm" color={r.unused.length > 0 ? 'yellow' : 'gray'} variant="light">
          {r.unused.length}
        </Badge>
      </Tooltip>
    ),
  },
  {
    id: 'coverage',
    label: 'Coverage',
    hint: '% of declared events that were used at least once in this window.',
    align: 'right',
    render: (r) => (
      <Badge size="sm" color={r.coverage >= 70 ? 'teal' : r.coverage >= 40 ? 'yellow' : 'red'} variant="light">
        {r.coverage}%
      </Badge>
    ),
  },
  {
    id: 'totalHandoffs',
    label: 'Total handoffs',
    hint: 'Total sibling-session handoffs observed for this bot in the window.',
    align: 'right',
    render: (r) => <Text size="sm">{r.totalHandoffs}</Text>,
  },
];

const DEFAULT_VISIBLE = ['match', 'drift', 'unused', 'coverage', 'totalHandoffs'];

export function SpecVsReality({ data }) {
  const sessions = data?.sessions ?? [];
  const tasks = data?.tasks ?? [];

  const rows = useMemo(() => {
    const edges = transitionsFromSessions(sessions, tasks);
    // Build: fromBot → { destBotName: count }
    const byBot = new Map();
    for (const e of edges) {
      if (!byBot.has(e.fromBot)) byBot.set(e.fromBot, new Map());
      const inner = byBot.get(e.fromBot);
      inner.set(e.toBot, (inner.get(e.toBot) ?? 0) + e.count);
    }

    // Walk catalog's autonomous bots that declared any custom-event exits
    const catalogBots = Object.values(catalogData.bots ?? {});
    const result = [];
    for (const b of catalogBots) {
      const declared = b.declared_custom_events ?? [];
      if (declared.length === 0) continue; // not a router

      const observed = byBot.get(b.bot_name) ?? new Map();

      // Match each observed destination to a declared event (if any).
      const matches = [];
      const drift = [];
      const destinationCovered = new Set(); // declared events seen at least once
      for (const [dest, count] of observed.entries()) {
        const matchedEvent = declared.find((e) => matchesEvent(dest, e));
        if (matchedEvent) {
          matches.push({ event: matchedEvent, destination: dest, count });
          destinationCovered.add(matchedEvent);
        } else {
          drift.push({ destination: dest, count });
        }
      }
      const unused = declared.filter((e) => !destinationCovered.has(e));
      const totalHandoffs = [...observed.values()].reduce((n, c) => n + c, 0);
      const coverage = declared.length > 0
        ? Math.round((destinationCovered.size / declared.length) * 100)
        : 0;

      result.push({
        key: b.bot_name,
        bot: b.bot_name,
        declaredCount: declared.length,
        matches, drift, unused,
        coverage, totalHandoffs,
      });
    }

    return result.sort((a, b) => b.totalHandoffs - a.totalHandoffs);
  }, [sessions, tasks]);

  const totalDriftRows = rows.reduce((n, r) => n + r.drift.length, 0);
  const totalUnusedRows = rows.reduce((n, r) => n + r.unused.length, 0);

  const csvRows = rows.map((r) => ({
    bot: r.bot,
    declared_events_count: r.declaredCount,
    match_count: r.matches.length,
    drift_count: r.drift.length,
    unused_count: r.unused.length,
    coverage_pct: r.coverage,
    total_handoffs: r.totalHandoffs,
    matches: r.matches.map((m) => `${m.event}→${m.destination}(${m.count})`).join('; '),
    drift: r.drift.map((d) => `${d.destination}(${d.count})`).join('; '),
    unused: r.unused.join('; '),
  }));

  return (
    <Stack gap="xs">
      <ReportTable
        title="Spec vs Reality — declared custom-event exits vs observed sibling handoffs"
        subtitle={
          `${rows.length} router bot${rows.length === 1 ? '' : 's'} with declared custom events · ` +
          `${totalDriftRows} drift row${totalDriftRows === 1 ? '' : 's'} · ` +
          `${totalUnusedRows} unused declared event${totalUnusedRows === 1 ? '' : 's'}. ` +
          `Hover each badge for the matched / drifted / unused destinations.`
        }
        reportId={REPORT_ID}
        columns={COLUMNS}
        defaultVisibleIds={DEFAULT_VISIBLE}
        rows={rows}
        emptyLabel="No router bots in catalog, or no sibling handoffs in window."
        csvExport={{
          filename: `spec-vs-reality-${new Date().toISOString().slice(0, 10)}.csv`,
          rows: csvRows,
        }}
      />
      <Alert color="gray" variant="light" icon={<IconInfoCircle size={14} />}>
        <Text size="xs">
          Catalog source: <code>wxccAiOps/data/botCatalog.json</code> (generated{' '}
          {catalogData.generatedAt ?? '(unknown)'}). If a bot's declared events don't match what's in
          this report, the catalog may be stale — re-run the parse against the latest exported bot
          JSONs. Future iteration: upload-replace via the Bot Catalog page without a rebuild.
        </Text>
      </Alert>
    </Stack>
  );
}
